import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTable, useSortBy, useGlobalFilter } from "react-table";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import GlobalFilter from "../components/common/filter";
import ReactPaginate from "react-paginate";
import axios from "axios";
import { url } from "../constants/url";

const PrivateSuites = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;
  const navigate = useNavigate();
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${url}/api/admin/private-suites?page=${
            currentPage + 1
          }&limit=${pageSize}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // If unauthorized, clear auth and redirect
        if (response.status === 401) {
          localStorage.clear();
          navigate("/login");
          return;
        }

        const result = response.data;
        const raw = result.data || result;

        const mapped = raw.map((suite) => {
          const createdAt = suite.createdAt;
          return {
            id: suite.id,
            fullName: suite.fullName || suite.name || "N/A",
            UserInformation: suite.UserInformation,
            createdAt: createdAt
              ? new Date(createdAt).toLocaleDateString()
              : "N/A",
            joiningDateRaw: createdAt ? new Date(createdAt).getTime() : 0,
            isSuspended: suite.isSuspended || false,
            hasSubscription: Array.isArray(suite.Subscription)
              ? suite.Subscription.length > 0
              : Boolean(suite.Subscription)
          };
        });

        setData(mapped);
        setPageCount(result.totalPages || 0);
      } catch (err) {
        // If error from axios with a 401 code
        if (err.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
        } else {
          console.error("Error fetching data:", err);
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage]);

  const handleSuspend = useCallback(async (id) => {
    if (!window.confirm("Suspend this suite?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${url}/api/admin/private-suites/${id}/suspend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If somehow axios doesn't throw but returns 401
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }

      // Success: mark suspended
      setData((d) =>
        d.map((s) => (s.id === id ? { ...s, isSuspended: true } : s))
      );
    } catch (err) {
      // Handle unauthorized
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      console.error("Error suspending suite:", err);
      // you could show an error toast here
    }
  }, []);

  const handleUnsuspend = useCallback(async (id) => {
    if (!window.confirm("Unsuspend this suite?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${url}/api/admin/private-suites/${id}/unsuspend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If unauthorized, clear auth and redirect
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }

      setData((d) =>
        d.map((s) => (s.id === id ? { ...s, isSuspended: false } : s))
      );
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      console.error("Error unsuspending suite:", err);
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Delete this suite?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${url}/api/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // If unauthorized, clear auth and redirect
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }

      setData((d) => d.filter((s) => s.id !== id));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      console.error("Error deleting suite:", err);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Suite Name",
        accessor: "fullName",
        Cell: ({ row, value }) => (
          <Link
            to={`/private-suite/${row.original.id}`}
            className="text-blue-500"
          >
            {value}
          </Link>
        )
      },
      {
        Header: "Location",
        accessor: (r) =>
          r.UserInformation
            ? `${r.UserInformation.city}, ${r.UserInformation.state}`
            : "N/A"
      },
      {
        Header: "Joining Date",
        accessor: "joiningDateRaw", // sorting by raw timestamp
        Cell: ({ row }) => row.original.createdAt
      },
      {
        Header: "Status",
        accessor: "isSuspended",
        Cell: ({ value }) =>
          value ? (
            <span className="px-2 py-1 bg-red-500 text-white rounded-full">
              Suspended
            </span>
          ) : (
            <span className="px-2 py-1 bg-green-500 text-white rounded-full">
              Active
            </span>
          )
      },
      {
        Header: "Subscription",
        accessor: "hasSubscription",
        Cell: ({ value }) => (value ? "Yes" : "No")
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <button
            onClick={() =>
              row.original.isSuspended
                ? handleUnsuspend(row.original.id)
                : handleSuspend(row.original.id)
            }
            className="text-blue-500 hover:underline"
          >
            {row.original.isSuspended ? "Unsuspend" : "Suspend"}
          </button>
        )
      },
      {
        Header: "Delete",
        Cell: ({ row }) => (
          <button
            onClick={() => handleDelete(row.original.id)}
            className="text-red-500 hover:underline"
          >
            Delete
          </button>
        )
      }
    ],
    [handleSuspend, handleUnsuspend, handleDelete]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    preGlobalFilteredRows
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [{ id: "joiningDateRaw", desc: true }]
      }
    },
    useGlobalFilter,
    useSortBy
  );

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl mb-4">Loading Private Suites…</h1>
        <div className="animate-pulse space-y-2">
          {Array(pageSize)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="h-8 bg-gray-300 rounded" />
            ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-red-500">Error: {error.message}</div>;
  }

  return (
    <div className="p-6 bg-white rounded-2xl shadow">
      <h1 className="text-2xl mb-4">Private Suites</h1>
      <GlobalFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <table {...getTableProps()} className="min-w-full mt-4">
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th
                  {...col.getHeaderProps(col.getSortByToggleProps())}
                  className="px-3 py-2 border-b"
                >
                  <div className="flex items-center">
                    {col.render("Header")}
                    <span className="ml-1">
                      {col.isSorted ? (
                        col.isSortedDesc ? (
                          <FaSortDown />
                        ) : (
                          <FaSortUp />
                        )
                      ) : (
                        <FaSort />
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} className="hover:bg-gray-50">
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} className="px-3 py-2 border-b">
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-4">
        <ReactPaginate
          pageCount={pageCount}
          onPageChange={handlePageClick}
          previousLabel="‹"
          nextLabel="›"
          breakLabel="…"
          containerClassName="flex justify-center space-x-2"
          pageClassName="px-3 py-1 border rounded cursor-pointer"
          activeClassName="bg-blue-500 text-white"
          forcePage={currentPage}
        />
      </div>
    </div>
  );
};

export default PrivateSuites;
